import { Fragment, useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import EditUser from './components/accounts/EditUser';
import CreateUser from './components/accounts/CreateUser';

import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { getStateByName } from "../../utils";

import Button from '@mui/material/Button';
import { parseToEasternTime } from './../../utils/dates';
import { useGetAccountsPagedQuery } from '../../services/accountsApi';
import DebounceInput from '../../components/DebounceInput';
import { Checkbox } from '@mui/material';

function tableCellCity(city, state) {
  let stateData = getStateByName(state) || {};
  let stateAbbrevation = stateData.abbreviation || "";

  return (
    <div className="d-flex align-items-center">
      <div>
        <p className="name-font">{city + ", " + stateAbbrevation}</p>
      </div>
    </div>
  )
}

function getLastSignIn(params) {
  let { lastSignIn } = params.row;
  return lastSignIn ? parseToEasternTime(lastSignIn) : null
}

function StatusButton({ status }) {
  return (
    <button
      style={{ cursor: 'auto' }}
      className={`button button-${status === "Inactive"
        ? "inactive"
        : status === "Active"
          ? "active"
          : status === "pending"
            ? "pending"
            : "unverified"
        }`}
    >
      {status}
    </button>
  )
}

function getName(params) {
  return params.row.firstName + " " + params.row.lastName;
}

function getDefaultsValues(searchParams) {
  const value = searchParams.get("showInactive");
  // const showInactive = value === "false" ? false : Boolean(value); //A: this is becasue Boolean("false") is true

  return {
    pageNumber: Number(searchParams.get("pageNumber")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 100,
    search: searchParams.get("accountSearch") || ""
  }
}

export default function Accounts() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const history = useHistory();

  const [userIdOnEditing, setUserIdOnEditing] = useState(null);
  const [createUserModalIsOpen, setCreateUserModalIsOpen] = useState(false);

  const { pageNumber: pageNumberDflt, pageSize: pageSizeDflt, search: searchDflt } = getDefaultsValues(searchParams);

  const [pageNumber, setPageNumber] = useState(pageNumberDflt);
  const [pageSize, setPageSize] = useState(pageSizeDflt);
  const [search, setSearch] = useState(searchDflt);
  const [showDeletedUsers, setDeletedUsers] = useState(false);

  const { data: usersApiRes, isLoading, isFetching, refetch } = useGetAccountsPagedQuery({
    isDeleted: showDeletedUsers,
    pageNumber,
    pageSize,
    search
  });

  const [columns, setColumns] = useState([
    { headerName: 'ID', field: 'id', hide: 'true', flex: 0.5, },
    {
      headerName: 'NAME', field: 'name', flex: 1,
      valueGetter: getName
    },
    {
      headerName: 'EMAIL', field: 'email', flex: 1,
    },
    {
      headerName: 'CITY-STATE', field: 'city', flex: 1,
      renderCell: (params) => {
        let { city, state } = params.row;
        return tableCellCity(city, state)
      }
    },
    {
      headerName: 'LAST SIGN IN', field: 'LastSignIn', valueGetter: getLastSignIn, flex: 1,
    },
    {
      headerName: 'STATUS', field: 'status', flex: 0.5,
      renderCell: (params) => {
        let { status } = params.row;
        return <StatusButton status={status} />
      }
    },
    {
      headerName: 'DISTRIBUTION', field: 'userDistribution', flex: 0.5,
      renderCell: (params) => {
        return params.row.userDistribution ? <CheckIcon sx={{ color: "var(--green)", margin: "auto" }} /> : <CloseIcon sx={{ color: "var(--red)", margin: "auto" }} />
      }
    },
    {
      headerName: 'SWARMS', field: 'useSwarm', flex: 0.5,
      renderCell: (params) => {
        return params.row.useSwarm ? <CheckIcon sx={{ color: "var(--green)", margin: "auto" }} /> : <CloseIcon sx={{ color: "var(--red)", margin: "auto" }} />
      }
    },
    { headerName: 'ROLE', field: 'role', flex: 0.5, },
    {
      headerName: '', field: 'edit', flex: 0,
      aggregable: false,
      sortable: false,
      groupable: false,
      filterable: false,
      renderCell: (params) => {
        return <button className="edit-button" onClick={() => onRowClick(params.row)}>
          <EditOutlinedIcon fontSize='small' /> Edit</button>
      }
    },
  ]);

  function onRowClick(userOnEditing) {
    setUserIdOnEditing(userOnEditing.id);
  }

  function handleEditUserModalClose() {
    if (userIdOnEditing) {
      setUserIdOnEditing(null);
    }
    if (createUserModalIsOpen) {
      setCreateUserModalIsOpen(false);
    }
  }

  const handleSearchChange = (newSearchValue) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.set('accountSearch', newSearchValue);
    history.push(`${window.location.pathname}?${newSearchParams.toString()}`);
    setSearch(newSearchValue);
  };

  function onPaginationChangeHandler(model) {
    console.log(model)
    const { page, pageSize: newPageSize } = model;
    if (newPageSize != pageSize) setPageSize(newPageSize);
    let pageGridFixed = page + 1;
    if (pageGridFixed != pageNumber) {
      setPageNumber(pageGridFixed)
    }
  }

  //update the url with the query params in the state
  useEffect(() => {
    searchParams.set("pageNumber", pageNumber);
    searchParams.set("pageSize", pageSize);
    searchParams.set("accountSearch", search);

    history.push(`/dashboard/admintools?${searchParams.toString()}`);
  }, [pageNumber, pageSize, search, history]);

  return (
    <Fragment>
      <div className='tab-title-container' style={{ margin: '0' }} >
        <h3 style={{ margin: '0 0.5em 0 0' }}>Account Status</h3>
      </div>

      <EditUser
        userId={userIdOnEditing}
        modalIsOpen={userIdOnEditing}
        closeModal={handleEditUserModalClose}
      />

      <CreateUser
        modalIsOpen={createUserModalIsOpen}
        closeModal={handleEditUserModalClose}
      />

      <div style={{ height: '70vh', width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', width: '100%', marginBottom: '0.75em' }}>
          {/* search box input */}
          <div style={{ width: '25%', margin: '0 0.5em 0 0' }}>
            <DebounceInput
              sx={{ width: '100%' }}
              defaultValue={searchDflt}
              placeholder="Search..."
              debounceTimeout={500}
              handleDebounce={handleSearchChange}
            />
          </div>

          <div style={{ margin: "0", width: '400px', display: 'flex', alignItems: 'center' }}>
            <Checkbox
              checked={showDeletedUsers}
              onChange={() => {setDeletedUsers(!showDeletedUsers)}}
              inputProps={{ 'aria-label': 'primary checkbox' }}
              id="showDeletedUsers"
            />
            <label htmlFor="showDeletedUsers">
              Show Deleted Users
            </label>
          </div>

          <div className='table-option-container'>
            <Button variant='outlined' style={{ marginBottom: '0.5em', whiteSpace: 'nowrap' }} onClick={() => setCreateUserModalIsOpen(true)}>Add New Account</Button>
          </div>
        </div>

        <DataGridPremium
          sx={{ borderRadius: "1em", backgroundColor: 'white', padding: '1em' }}
          rows={(usersApiRes?.items || [])} columns={columns}
          loading={isLoading || isFetching}
          disableSelectionOnClick
          pagination
          paginationMode="server"
          rowCount={usersApiRes?.totalCount || 0}
          onPaginationModelChange={onPaginationChangeHandler}
          page={pageNumber - 1} //A: the pagination starts in 0, but the api starts in 1
          pageSize={pageSize}
          initialState={{
            pagination: { paginationModel: { pageSize: pageSizeDflt, page: pageNumberDflt - 1 } }, //A: the pagination starts in 0, but the api starts in 1
          }}
        />
      </div>

    </Fragment>
  )
}